import React, { useState } from "react";
import Paper from "@/components/paper/Paper";
import { useTranslation } from "react-i18next";
import MediaCardContainer from "@/app/pages/companyProfile/addedBy/MediaContainer";
import PaymentContainer from "@/app/pages/companyProfile/addedBy/PaymentContainer";
import HoursOfOperation from "@/app/pages/companyProfile/addedBy/HoursOfOperation";
import SocialLinks from "@/app/pages/companyProfile/addedBy/SocialLinks";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const CompanyAddedBy = ({ companyEnhancedFields, contacts }): JSX.Element => {

    const [openSections, setOpenSections] = useState({});
    const toggleAccordion = (section) => {
        setOpenSections((prevState) => ({
            ...prevState,
            [section]: !prevState[section],
        }));
    };

    const { t } = useTranslation();

    const languageMap = {
        ZH: "中文 (Chinese)",
        EN: "English",
        FL: "Suomi",
        DE: "Deutsch (German)",
        HI: "हिन्दी (Hindi)",
        IT: "Italiano (Italian)",
        JA: "日本語 (Japanese)",
        FR: "Français (French)"
    };
    const socialMediaData = {
        linkedinUrl : companyEnhancedFields?.linkedinUrl || null,
        tiktokUrl:companyEnhancedFields?.tiktokUrl  || null,
        instagramUrl:companyEnhancedFields?.instagramUrl || null,
        twitterUrl:companyEnhancedFields?.twitterUrl || null,
        facebookUrl:companyEnhancedFields?.facebookUrl || null,
        yelpUrl:companyEnhancedFields?.yelpUrl || null,
    }
    const socialMediaCheck = !Object.values(socialMediaData).every(value => value === null);
    return (
        <>
            <a className="anchor" data-name="added-by-anchor" href="/added-by-anchor">
                <span className="display-hidden">Added By</span>
            </a>
            <Paper
                additionalContainerClass="headerDesign"
                title={
                    t("companyProfile.addedBy.titleLocalized") +
                    " " +
                    `${contacts?.companyName}`

                }
            >
                <div
                    className="company-parent-container"
                >
                    <div className="company-added-row">
                        {contacts?.companyName && <div className="company-section">
                            <div className="company-sub-section-left">
                                <p className="company-info">
                                    {`This information is updated and provided by ${contacts?.companyName}. The content available on the D&B Business Directory is provided "as-is" and "as-available" and may not be reviewed or validated by Dun & Bradstreet. Dun & Bradstreet disclaims any liability for information made available on the D&B Business Directory.`}
                                </p>
                            </div>
                        </div>}
                        {companyEnhancedFields?.logoUrls && <div className="company-section">
                            <div className="company-sub-section-left">
                                <p className="addedByMedia">
                                    {t("companyProfile.addedBy.companyLogo")}
                                </p>
                            </div>
                            <div className="company-sub-section-right">
                                <div className="company-list">
                                    <h4 className="company-item">
                                        <img src={companyEnhancedFields?.logoUrls[0]} alt="dnbLogo" className="company-icon" />
                                    </h4>
                                </div>
                            </div>
                        </div>}
                        {socialMediaCheck && <div className="company-section">
                                <button
                                    className="company-sub-section-left"
                                    onClick={() => toggleAccordion('socialMedia')}
                                >
                                    <p className="addedByMedia">
                                        {t("companyProfile.addedBy.socialMedia")}
                                    </p>
                                    <FontAwesomeIcon
                                        icon={openSections['socialMedia'] ? faChevronUp : faChevronDown}
                                        className="accordion-icon"
                                    />
                                </button>
                                <div
                                    className={`company-sub-section-right ${openSections['socialMedia'] ? 'open' : 'closed'}`}
                                >
                                    <div className="company-list">
                                        <SocialLinks companyEnhancedData={socialMediaData} />
                                    </div>
                                </div>
                        </div>}
                        
                        {companyEnhancedFields?.languages && <div className="company-section">
                            <button
                                className="company-sub-section-left"
                                onClick={() => toggleAccordion('languages')}
                            >
                                <p className="addedByMedia">
                                    {t("companyProfile.addedBy.languages")}
                                </p>
                                <FontAwesomeIcon
                                    icon={openSections['languages'] ? faChevronUp : faChevronDown}
                                    className="accordion-icon"
                                />
                            </button>
                            <div className={`company-sub-section-right ${openSections['languages'] ? 'open' : 'closed'}`} >
                                <div className="company-list">
                                    {companyEnhancedFields?.languages.map((code) => (
                                        <span key={code} className="company-item">
                                            {languageMap[code]}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>}
                        {companyEnhancedFields?.hoursOfOperation && <div className="company-section">
                            <button
                                className="company-sub-section-left"
                                onClick={() => toggleAccordion('operationhours')}
                            >
                                <p className="addedByMedia">
                                    {t("companyProfile.addedBy.hoursOfOperation")}
                                </p>
                                <FontAwesomeIcon
                                    icon={openSections['operationhours'] ? faChevronUp : faChevronDown}
                                    className="accordion-icon"
                                />
                            </button>
                            <div className={`company-sub-section-right ${openSections['operationhours'] ? 'open' : 'closed'}`} >
                                <div className="company-list">
                                    <HoursOfOperation
                                        hoursOfOperation={companyEnhancedFields?.hoursOfOperation}
                                    />
                                </div>
                            </div>
                        </div>}
                        {companyEnhancedFields?.paymentTypeCodes && <div className="company-section">
                            <button
                                className="company-sub-section-left"
                                onClick={() => toggleAccordion('payments')}
                            >
                                <p className="addedByMedia">
                                    {t("companyProfile.addedBy.paymentAccepted")}
                                </p>
                                <FontAwesomeIcon
                                    icon={openSections['payments'] ? faChevronUp : faChevronDown}
                                    className="accordion-icon"
                                />
                            </button>
                            <div className={`company-sub-section-right ${openSections['payments'] ? 'open' : 'closed'}`} >
                                <div className="company-list">
                                    <PaymentContainer
                                        paymentCodes={companyEnhancedFields?.paymentTypeCodes}
                                    />
                                </div>
                            </div>
                        </div>}
                        {companyEnhancedFields?.videoUrls || companyEnhancedFields?.galleryPhotoUrls  && <div className="company-section">
                            <button
                                className="company-sub-section-left"
                                onClick={() => toggleAccordion('media')}
                            >
                                <p className="addedByMedia">
                                    {t("companyProfile.addedBy.photosVideos")}
                                </p>
                                <FontAwesomeIcon
                                    icon={openSections['media'] ? faChevronUp : faChevronDown}
                                    className="accordion-icon"
                                />
                            </button>
                            <div className={`company-sub-section-right ${openSections['media'] ? 'open' : 'closed'}`} >
                                <MediaCardContainer
                                    companyEnhancedFields={companyEnhancedFields}
                                />
                            </div>
                        </div>}
                    </div>
                </div>
            </Paper>
        </>
    );
};

export default CompanyAddedBy;
